import { createColumnHelper, Row } from "@tanstack/react-table";
import { JSX, useMemo } from "react";
import { CSSProperties } from "react";
import { ButtonTypes, createTableStyle, ListData_ValueTypes, SelectFieldOption_Values, TableStyles_Types, ValueSteps } from "../../../Definitions";
import { IdentifiableRow } from "../reactiveTableRenderer";
import { ButtonTableCell, CheckboxTableCell, CreatableListTableCell, DateTableCell, DisplayCell, DivTableCell, EditAbleCell, HeadCell, ListTableCell, NumberTableCell, ProgressTableCell, StringTableCell } from "../";
import { UNIT } from "../../BaseComponents";
import { colors, glyphs } from "../../../Styles";
import NewButton from "../../BaseComponents/Buttons/newButton";
import { MutableTableCell } from "../CellTypes/mutableTableCell";

export interface GenericRow extends IdentifiableRow {
  [key: string] : string | number | boolean | JSX.Element | undefined | SelectFieldOption_Values | Date 
}

export interface GenericDataParameters<T = GenericRow> extends DivCellDataParameters {
  accessor: string;
  type: ListData_ValueTypes;
  width?: string;
  unit?: Units;
  headerValue: string | JSX.Element;
  idForColor?: number;
  sort?: boolean;
  filter?: boolean;
  options?: SelectFieldTableOption<T>;
  valueStep?: ValueSteps;
  regex?: boolean;
  maxValueLength?: number | ((rowId: number) => number);
  maxValueNumber?: number | ((rowId: number) => number);
  minValueNumber?: (rowId: number) => number;
  forceRerenderOnTableEdit?: true;
  focus_view?: boolean;
  staticColumn?: boolean | ((row?: Row<T>) => boolean);
  onClick?: () => void;
  onHoverContent?: JSX.Element;
  displaySeconds?: boolean
}

export type SelectFieldTableOption<T> = SelectFieldOption_Values[] | ((row?: Row<T>) => SelectFieldOption_Values[]) 

interface DivCellDataParameters {
  divStyleOverride?: (value?: number|string) => CSSProperties | undefined,
  styleOverrideOnValue?: boolean,
}

export interface GenericColumnConstructorProps<T = GenericRow> {
  accessorData: GenericDataParameters<T>[],
  tableStyle: TableStyles_Types,
}

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    focus_view?: boolean;
  }
}

export enum Units {
  percent = "Percent",
  kg = "kg",
  energy = "energy"
}

// export interface EditDataParameters<T> extends GenericDataParameters<T> {
  // staticColumn?: boolean,
  // options?: SelectFieldTableOption<T>,
  // onClick?: () => void;
  // valueStep?: ValueSteps
  // focus_view?: boolean
// }

// interface ColumnConstructorProps<T> extends Omit<GenericColumnConstructorProps, "accessorData"> {
//   accessorData: EditDataParameters<T>[]
// } 

export const useColumnConstructor = <T extends GenericRow>({accessorData, tableStyle, canEdit}: GenericColumnConstructorProps<T> & EditAbleCell<T>) => {
  const columnHelper = createColumnHelper<T>();
  const {headCellStyle, bottomCellStyle} = useMemo(() => createTableStyle(tableStyle),[tableStyle])

  return useMemo( () => accessorData.map(({ accessor, unit, staticColumn=false, type, headerValue, idForColor, sort, options, width, divStyleOverride, styleOverrideOnValue, onClick, onHoverContent, valueStep, regex, maxValueNumber, minValueNumber, maxValueLength, forceRerenderOnTableEdit, focus_view=false }) => {
    const footerValue = unit === Units.kg ? <UNIT content={"Kg"} color={colors.greyLightTertiary} glyph={glyphs.Kg({ size: "15px" })} /> : unit === Units.energy ? <UNIT content={"Energy"} color={colors.greyLightTertiary} glyph={glyphs.Energy({ size: "15px" })} /> : unit === Units.percent ? <UNIT content={"Pct"} color={colors.greyLightTertiary} glyph={glyphs.Percent({ size: "15px" })} /> : "";
    switch (type) {
      default: 
      case ListData_ValueTypes.String:
        return columnHelper.accessor(
          row => (row[accessor] as string) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => StringTableCell<T>({...props, canEdit, staticColumn, focus_view: focus_view}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: {width: width},
            filterFn: "equalsString",
          }
        )
      case ListData_ValueTypes.Number:
        return columnHelper.accessor(
          row => (row[accessor] as number) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onHoverContent={onHoverContent ? onHoverContent : undefined} onClick={onClick ? onClick : (!canEdit && sort ? props.column.getToggleSortingHandler() : undefined)} keyPass={props.header.id+"header"}/>),
            cell: (props) => NumberTableCell({...props, canEdit, staticColumn, valueStep: valueStep ?? ValueSteps.floats, maxValueNumber: maxValueNumber ?? undefined, minValueNumber }),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "basic" : undefined,
            meta: {width: width, forceRerenderOnTableEdit: forceRerenderOnTableEdit ?? undefined},
            sortDescFirst: false,
            filterFn: "equals"
          }
        )
        case ListData_ValueTypes.Mutable:
          return columnHelper.accessor(
            row => (row[accessor] as number) ?? "",
            {
              id: accessor,
              header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onHoverContent={onHoverContent ? onHoverContent : undefined} onClick={onClick ? onClick : (!canEdit && sort ? props.column.getToggleSortingHandler() : undefined)} keyPass={props.header.id+"header"}/>),
              cell: (props) => MutableTableCell({...props, canEdit, staticColumn, valueStep: valueStep ?? ValueSteps.floats, maxValueNumber: maxValueNumber ?? undefined, minValueNumber }),
              footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
              sortingFn: sort ? "basic" : undefined,
              meta: {width: width, forceRerenderOnTableEdit: forceRerenderOnTableEdit ?? undefined},
              sortDescFirst: false,
              filterFn: "equals"
            }
          )
      case ListData_ValueTypes.Check:
        return columnHelper.accessor(
        row => row[accessor] as boolean,
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => CheckboxTableCell({...props, canEdit, staticColumn}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "basic" : undefined,
            meta: {width: width}
          }
        )
      case ListData_ValueTypes.List:
        return columnHelper.accessor(
          row => (row[accessor] as SelectFieldOption_Values) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => ListTableCell<T>({...props, canEdit, staticColumn, divStyleOverride, styleOverrideOnValue, maxValueLength, focus_view: focus_view, options: options ? options : []}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? (listItemA, listItemB, columnId) => {
              return ((listItemA.original[columnId] as SelectFieldOption_Values) || {label:""}).label.localeCompare(((listItemB.original[columnId] as SelectFieldOption_Values)|| {label: ""}).label )
            } : undefined,
            meta: {width: width},
            filterFn: (row, columnId, filterValue: SelectFieldOption_Values[]) => {
              const value = row.getValue<SelectFieldOption_Values>(columnId);
              return filterValue.map((filterParameter) => (filterParameter.id)).includes(value?.id as number)
            }
          }
        )
      case ListData_ValueTypes.CreatableList:
        return columnHelper.accessor(
          row => (row[accessor] as SelectFieldOption_Values) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => CreatableListTableCell<T>({...props, canEdit, staticColumn, regex, divStyleOverride, styleOverrideOnValue, maxValueLength, options: options ? options : []}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: {width: width},
            filterFn: (row, columnId, filterValue: SelectFieldOption_Values[]) => {
              const value = row.getValue<SelectFieldOption_Values>(columnId);
              return filterValue.map((filterParameter) => (filterParameter.id)).includes(value?.id as number)
            }
          }
        )
      case ListData_ValueTypes.Div:
        return columnHelper.accessor(
          row => (row[accessor] as JSX.Element) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: props => DivTableCell({...props, canEdit, staticColumn, divStyleOverride, styleOverrideOnValue}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={""} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: { 
              width: width,
             
            }
          }
        )
        case ListData_ValueTypes.Display:
          return columnHelper.accessor(
            row => (row[accessor] as string) ?? "",
            {
              id: accessor,
              header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
              cell: (props) => DisplayCell({...props, canEdit, staticColumn, divStyleOverride, styleOverrideOnValue}),
              footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
              sortingFn: sort ? "alphanumeric" : undefined,
              meta: {width: width},
            }
          )
        case ListData_ValueTypes.ProgressBar:
          return columnHelper.accessor(
            row => (row[accessor] as JSX.Element) ?? "",
            {
              id: accessor,
              header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
              cell: props => ProgressTableCell({...props, canEdit, staticColumn}),
              footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
              sortingFn: sort ? (rowA,rowB,columnId) => {
                if ((rowA.original[columnId] as JSX.Element)?.props["progressed"] && (rowB.original[columnId] as JSX.Element)?.props["progressed"]) {
                  return (rowA.original[columnId] as JSX.Element).props["progressed"] - (rowB.original[columnId] as JSX.Element).props["progressed"]  
                }
                if ((rowA.original[columnId] as JSX.Element)?.props["progressed"]) {
                  return 1;
                }
                return 0;
              } : undefined,
              meta: {width: width},
              filterFn: (row, columnId, filterValue: SelectFieldOption_Values[]) => {
                const value = row.getValue<JSX.Element>(columnId);
                const total = value.props["total"]
                const progressed =  value.props["progressed"]
                const selectedFilter = filterValue[0].id
                if (progressed < 0) {
                  return false;
                }
                if(total) {
                  if (typeof selectedFilter === "number" && (1-(progressed / total))*100 > selectedFilter) {
                    return true
                  }
                } 

                return false;
              }
            }
          )
        case ListData_ValueTypes.Button:
          return columnHelper.accessor(
          row => row[accessor] as JSX.Element,
            {
              id: accessor,
              header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
              cell: (props) => ButtonTableCell({...props, canEdit, staticColumn }),
              footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
              sortingFn: sort ? "basic" : undefined,
              meta: {width: width, forceRerenderOnTableEdit: true},
              filterFn: (row, columnId, filterValue: SelectFieldOption_Values[]) => {
              
                const value = row.getValue<JSX.Element | number>(columnId);
                
                if (typeof value === "number") {
                  return filterValue.map((filterParameter) => (filterParameter.id)).includes(value)
                }
              
                const content = value.props["content"];
                if (content && typeof content === "string") {
                  return filterValue.map((filterParameter) => String(filterParameter.id)).includes(content)
                }
                return false;
              }
            }
          ) 
      case ListData_ValueTypes.DeleteRowAction:
        const checkDeletePossible = (row: Row<T>, checkFunction?: (row: Row<T>) => boolean) => {
          if (checkFunction) {
            return checkFunction(row);
          }
          return true;
        }
        return columnHelper.display(
          { 
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} key={"removeRowActionHeader"} keyPass="removeRowActionHeader" style={headCellStyle} cellValue={""}/>),
            cell: (props) => {
              return ((canEdit && checkDeletePossible(props.row, props.table.options.meta?.allowDeleteRowCheck)) ? (
                // <Button key={props.cell.id} content={""} onMouseUp={() => props.table.options.meta?.deleteRow(props.row.original.id)} buttonType={ButtonTypes.textButton} startEnhancer={glyphs.Delete({ fillColor: colors.greyDarkSecondary, size: "20px" })} />
                <NewButton
                  key={props.cell.id}
                  content={""}
                  onClick={() => props.table.options.meta?.deleteRow(props.row.original.id)}
                  buttonType={ButtonTypes.glyphButton}
                  startEnhancer={glyphs.Delete({ fillColor: colors.greyDarkSecondary, size: "20px" })}
                />
              ) : (<div key={props.cell.id}>{ }</div>))
            },
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={"removeRowActionFooter"} keyPass="removeRowActionFooter" style={bottomCellStyle} cellValue={""}/>),
            meta: {
              width: width,
              forceRerenderOnTableEdit: true,
            }
          }
        )
        case ListData_ValueTypes.Date:
          return columnHelper.accessor(row => (row[accessor] as Date),
          {
            id: accessor,
            header: (props) => (<HeadCell pinningStyle={tableStyle} {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => DateTableCell<T>({...props, canEdit, staticColumn, focus_view: focus_view}),
            footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "datetime" : undefined,
            meta: {width: width},
          }
        )
      }
    }
  ),[accessorData, bottomCellStyle, canEdit, columnHelper, headCellStyle, tableStyle])
}



// export const useGenericColumnConstructor = <T extends GenericRow,> ({accessorData, tableStyle}: GenericColumnConstructorProps) => {
//   const columnHelper = createColumnHelper<T>();
//   const getUnit = useUnitsComponent();
//   const {headCellStyle, bottomCellStyle} = createTableStyle(tableStyle)

//   return accessorData.map(({accessor, unit, type, headerValue, idForColor, sort, filter, width, focus_view}) => {
//     const footerValue = unit ? getUnit(unit) : "";
//     switch (type) {
//       default: 
//       case ListData_ValueTypes.String:
//         return columnHelper.accessor(
//           row => (row[accessor] as string) ?? "",
//           {
//             id: accessor,
//             header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
//             cell: (props) => (props.getValue()),
//             footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
//             sortingFn: sort ? "alphanumeric" : undefined,
//             meta: {width: width, focus_view: focus_view},
//             enableColumnFilter: filter ? true : false,
//             filterFn: filter ? "includesString" : undefined
//           }
//         )
//       case ListData_ValueTypes.Number:
//         return columnHelper.accessor(
//           row => (row[accessor] as number) ?? "",
//           {
//             id: accessor,
//             header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={ sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
//             cell: (props) => (props.getValue()),
//             footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
//             sortingFn: sort ? "basic" : undefined,
//             meta: {width: width, focus_view: focus_view},
//             sortDescFirst: false,
//           }
//         )
//       case ListData_ValueTypes.Check:
//         return columnHelper.accessor(
//         row => row[accessor] as boolean,
//           {
//             id: accessor,
//             header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
//             cell: (props) => SimpleCheckboxTableCell({...props }),
//             footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
//             sortingFn: sort ? "basic" : undefined,
//             meta: {width: width, focus_view: focus_view},
//           }
//         )
//       case ListData_ValueTypes.Div:
//         return columnHelper.accessor(
//         row => row[accessor] as string,
//           {
//             id: accessor,
//             header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
//             cell: (props) => SimpleDivTableCell({...props }),
//             footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
//             sortingFn: sort ? "basic" : undefined,
//             meta: {width: width, focus_view: focus_view},
//           }
//         ) 
//       case ListData_ValueTypes.List:
//         return columnHelper.accessor(
//         row => row[accessor] as string,
//           {
//             id: accessor,
//             header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
//             cell: (props) => SimpleDivTableCell({...props }),
//             footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
//             sortingFn: sort ? "basic" : undefined,
//             meta: {width: width, focus_view: focus_view},
//             filterFn: (row, columnId, filterValue) => {
//               const value = row.getValue<SelectFieldOption_Values>(columnId);
//               return value.id === filterValue.id;
//             } 
//           }
//         ) 
//         case ListData_ValueTypes.Display:
//           return columnHelper.accessor(
//           row => row[accessor] as string,
//             {
//               id: accessor,
//               header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
//               cell: (props) => SimpleDivTableCell({...props }),
//               footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
//               sortingFn: sort ? "basic" : undefined,
//               meta: {width: width, focus_view: focus_view},
//             }
//           ) 
//         case ListData_ValueTypes.Button:
//           return columnHelper.accessor(
//           row => row[accessor] as string,
//             {
//               id: accessor,
//               header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
//               cell: (props) => SimpleDivTableCell({...props }),
//               footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
//               sortingFn: sort ? "basic" : undefined,
//               meta: {width: width, focus_view: focus_view},
//             }
//           ) 
//       case ListData_ValueTypes.ProgressBar:
//         return columnHelper.accessor(
//         row => row[accessor] as string,
//           {
//             id: accessor,
//             header: (props) => (<HeadCell pinningStyle={tableStyle} sortArrow={sort} {...props} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
//             cell: (props) => SimpleProgressTableCell({...props }),
//             footer: (props) => (<HeadCell pinningStyle={tableStyle} {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
//             sortingFn: sort ? "alphanumeric" : undefined, // TODO: Sort on "remaining food"
//             meta: {width: width, focus_view: focus_view},
//           }
//         ) 
//       }
//     }
//   )
// }